// noinspection ExceptionCaughtLocallyJS

import { arrayBufferToBase64, base64ToArrayBuffer, getLocale } from '@/helpers';
import { logger } from '@/helpers/log.service';
import { determineHashAlgorithm, determineSignAlgorithm } from '@/sign/helpers/sign.helper';
import { SignPrivateKeyStrategy } from './sign-private-key.strategy';
import { SigningParams } from '@/sign/models/signing-params.model';
import { AppSignKeyOperationType, SignMethod, SignPurpose } from '@/sign/models/sign-constants';
import { depositSignService } from '@/services/deposit-sign.service';
import { MixWithOwnCertFromSigningParams } from '@/sign/mixins/deposit-sign.mixin';
import { MixWithDiiaSigning } from '@/sign/mixins/diia-sign.mixin';
import { disableShowMsg, enableShowMsg, setLoadingCancelCallback, setLoadingText } from '@/mixins/ui-mixin';
import { cloudKeySignService } from '@/services/cloud-key-sign.service';
import { MixWithCloudKeyFileSystemKeyType } from '../mixins/cloud-key-sign.mixin';
import { MixWithFillEndUserOwnerInfoFromSigns } from '../mixins/general-sign.mixin';
import { executeCloudSignOperationWithConfirmationByQrCode } from '../helpers/cloud-sign-qr.helper';
import { EventBus } from '@/helpers/event-bus';
import { getRecaptchaTokenIfNotAuthenticated } from '@/helpers/recaptha-helper';
import { utilityService } from '@/services/utility.service';

abstract class SignFilesPrivateKeyStrategy extends SignPrivateKeyStrategy {
  protected files: Array<any>;
  protected isSignHash: boolean;
  protected isInternal: boolean;
  protected resultAsBase64: boolean;
  protected ignoreErrors: boolean;

  constructor({ files, isSignHash, isInternal, resultAsBase64, ignoreErrors }) {
    super();
    this.files = files;
    this.isSignHash = !!isSignHash;
    this.isInternal = !!isInternal;
    this.resultAsBase64 = !!resultAsBase64;
    this.ignoreErrors = !!ignoreErrors;
    this.signPurpose = SignPurpose.Files;
  }

  public async execute(prms: SigningParams): Promise<any> {
    disableShowMsg();
    try {
      await super.execute(prms);

      await this.init();
      await this.validate();
      await this.determineKeyTypes();

      let signs: Array<any> = await this.signFiles();

      return { signs, info: this.signingParams.data?.endUserOwnerInfo };
    } finally {
      enableShowMsg();
    }
  }

  protected async signFiles(): Promise<Array<any>> {
    let signs: Array<any> = [];
    const signAlgo = determineSignAlgorithm(this.keyType);
    const hashAlgo = determineHashAlgorithm(this.keyType);

    for (let file of this.files) {
      try {
        let fileContent: Uint8Array | null;
        fileContent = this.getFileContent(file);
        if (!fileContent) {
          continue;
        }

        let isInternal = await this.isDataIsInternalSignature(fileContent);

        let sign: any;
        if (this.isSignHash) {
          let hash = await this.euSignLib.HashData(hashAlgo, fileContent, true);
          sign = await this.euSignHash(signAlgo, hash, true, this.resultAsBase64);
          if (isInternal) {
            const creationResult = await utilityService.createInternalNestedSign(arrayBufferToBase64(fileContent),
              this.resultAsBase64 ? sign : arrayBufferToBase64(sign));
            if (!creationResult || !creationResult.newSignature) {
              throw new Error('Failed to create signature');
            }
            sign = this.resultAsBase64 ? creationResult.newSignature : base64ToArrayBuffer(creationResult.newSignature);
          }
        } else {
          sign = await this.euSignData(signAlgo, fileContent, !isInternal, true, this.resultAsBase64);
        }
        signs.push({
          sign: sign,
          uid: file.uid
        });
      } catch (error) {
        if (this.ignoreErrors) {
          logger.error(`Signing file uid: ${file.uid} error: `, error);
          signs.push({
            uid: file.uid,
            error: error
          });
        } else {
          throw error;
        }
      }
    }
    return signs;
  }

  protected getFileContent(file: any): Uint8Array | null {
    let fileContent: Uint8Array | null = null;
    if (file) {
      if ('reader' in file) {
        fileContent = new Uint8Array(file.reader.result);
      } else if ('content' in file) {
        if (typeof file.content === 'string') {
          fileContent = base64ToArrayBuffer(file.content);
        } else if (file.content instanceof Uint8Array) {
          fileContent = file.content;
        }
      }
    }
    return fileContent;
  }

  protected euSignHash(
    signAlgo: number,
    hash: string | Uint8Array | Array<string | Uint8Array>,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return this.euSignLib.CtxSignHash(this.privateKeyContext, signAlgo, hash, appendCert, asBase64String);
  }

  protected euSignData(
    signAlgo: number,
    data: string | Uint8Array | Array<string | Uint8Array>,
    external: boolean,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return this.euSignLib.CtxSignData(this.privateKeyContext, signAlgo, data, external, appendCert, asBase64String);
  }

  protected async isDataIsInternalSignature(fileContent: Uint8Array): Promise<boolean> {
    let isInternal = this.isInternal;
    if (!isInternal) {
      try {
        await this.euSignLib.VerifyDataInternal(fileContent, 0);
        isInternal = true;
      } catch (getSignerErr) {
        // ignore
      }
    }
    return isInternal;
  }
}

export class SignFilesFileSystemStrategy extends SignFilesPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.FileSystem;
}

export class SignFilesDeviceStrategy extends SignFilesPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.Device;
}

export class SignFilesCloudStrategy extends SignFilesPrivateKeyStrategy {
  protected signMethod?: SignMethod = SignMethod.Cloud;

  constructor(prms: any) {
    super(prms);

    this.mobileAppName = prms?.mobileAppName;
  }
}

export class SignFilesDepositSignLoginPswStrategy
  extends MixWithOwnCertFromSigningParams(SignFilesCloudStrategy) {
  protected async signFiles(): Promise<Array<any>> {
    let signs: any;

    if (this.isSignHash) {
      signs = await this.signHashes();
    } else {
      signs = await this.signData();
    }
    return signs;
  }

  private async signHashes(): Promise<Array<any>> {
    let { depositSignUserSessionId, keyId, keyPassword } = this.signingParams.data;
    const hashAlgo = determineHashAlgorithm(this.keyType);
    const hashes: Array<{ id: string, data: string }> = [];

    let filesToSign: any[] = [];
    for (let file of this.files) {
      let fileContent = this.getFileContent(file);
      if (!fileContent) continue;

      let hash = await this.euSignLib.HashData(hashAlgo, fileContent, true);

      let isInternal = await this.isDataIsInternalSignature(fileContent);

      hashes.push({ id: file.uid, data: hash });

      filesToSign.push({ fileContent, id: file.uid, isInternal });
    }

    let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

    let signedHashes = await depositSignService.signHashes(depositSignUserSessionId, keyId, keyPassword, hashes,
      recaptchaToken, recaptchaType);

    for (const signedHash of signedHashes) {
      let fileToSign = filesToSign.find(h => h.id === signedHash.id);
      if (fileToSign.isInternal) {
        let creationResult = await utilityService.createInternalNestedSign(arrayBufferToBase64(fileToSign.fileContent), signedHash.sign);
        if (!creationResult || !creationResult.newSignature) {
          throw new Error('Failed to create signature');
        }
        signedHash.sign = creationResult.newSignature;
      }
    }

    return signedHashes.map((s: any) => ({ sign: this.resultAsBase64 ? s.sign : base64ToArrayBuffer(s.sign), uid: s.id }));
  }

  private async signData(): Promise<Array<any>> {
    let signs: Array<any> = [];
    let { depositSignUserSessionId, keyId, keyPassword } = this.signingParams.data;

    for (let file of this.files) {
      try {
        let fileContent = this.getFileContent(file);
        if (!fileContent) continue;

        let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

        let sign = await depositSignService.signData(depositSignUserSessionId, keyId, keyPassword,
          arrayBufferToBase64(fileContent), true, recaptchaToken, recaptchaType);

        signs.push({ sign: this.resultAsBase64 ? sign : base64ToArrayBuffer(sign), uid: file.uid });
      } catch (error) {
        if (this.ignoreErrors) {
          logger.error(`Signing file uid: ${file.uid} error: `, error);
          signs.push({
            uid: file.uid,
            error: error
          });
        } else {
          throw error;
        }
      }
    }

    return signs;
  }
}

export class SignFilesDiiaStrategy
  extends MixWithFillEndUserOwnerInfoFromSigns(MixWithDiiaSigning(SignFilesCloudStrategy)) {
  protected async signFiles(): Promise<Array<any>> {
    const hashAlgo = determineHashAlgorithm(this.keyType);

    const hashes: Array<{ uid: string, fileName: string, fileHash: string, idx: number }> = [];

    const filesToSign: any[] = [];
    let idx = 0;
    for (let file of this.files) {
      let fileContent = this.getFileContent(file);
      if (!fileContent) continue;

      let hash = await this.euSignLib.HashData(hashAlgo, fileContent, true);
      let fileName = file.file && file.file.name ? file.file.name : file.fileName;
      hashes.push({ uid: file.uid, fileName: fileName, fileHash: hash, idx });

      let isInternal = await this.isDataIsInternalSignature(fileContent);

      filesToSign.push({ fileContent, isInternal, idx });

      idx++;
    }

    this.makeFileNamesUnique(hashes);

    let signs = await this.signFileHashesWithDiia(hashes);

    await this.fillEndUserOwnerInfoFromSigns(signs);

    let mappedSigns: any[] = [];

    for (const s of signs) {
      let hash = hashes.find(h => h.fileName === s.name);
      if (hash) {
        let fileToSign = filesToSign.find(f => f.idx === hash!.idx);
        if (fileToSign.isInternal) {
          let creationResult = await utilityService.createInternalNestedSign(arrayBufferToBase64(fileToSign.fileContent), s.signature);
          if (!creationResult || !creationResult.newSignature) {
            throw new Error('Failed to create signature');
          }
          s.signature = creationResult.newSignature;
        }
      }
      mappedSigns.push({
        uid: hash?.uid,
        sign: this.resultAsBase64 ? s.signature : base64ToArrayBuffer(s.signature)
      });
    }
    return mappedSigns;
  }
}

export class SignFilesCloudKeyFileSystemStrategy
  extends MixWithFillEndUserOwnerInfoFromSigns(MixWithCloudKeyFileSystemKeyType(SignFilesCloudStrategy)) {
  protected async signFiles(): Promise<Array<any>> {
    let signs: any;
    if (this.isSignHash) {
      signs = await this.signHashes();
    } else {
      signs = await this.signData();
    }
    return signs;
  }

  private async signHashes(): Promise<Array<any>> {
    const hashAlgo = determineHashAlgorithm(this.keyType);
    const hashes: Array<string> = [];

    const filesToSign: Array<any> = [];
    for (let file of this.files) {
      let fileContent = this.getFileContent(file);
      if (!fileContent) continue;

      let hash = await this.euSignLib.HashData(hashAlgo, fileContent, true);
      hashes.push(hash);

      let isInternal = await this.isDataIsInternalSignature(fileContent);
      filesToSign.push({ file, isInternal, fileContent });
    }

    let { certificate, privateKeyPassword } = this.signingParams.data;

    let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

    let signs = await cloudKeySignService.signHashes(certificate, privateKeyPassword, hashes, recaptchaToken, recaptchaType);

    await this.fillEndUserOwnerInfoFromSigns(signs, (s) => s);

    for (let i = 0; i < signs.length; i++) {
      if (filesToSign[i].isInternal) {
        let creationResult = await utilityService.createInternalNestedSign(arrayBufferToBase64(filesToSign[i].fileContent), signs[i]);
        if (!creationResult || !creationResult.newSignature) {
          throw new Error('Failed to create signature');
        }
        signs[i] = creationResult.newSignature;
      }
    }

    return signs && signs.length
      ? signs.map((s: any, i: number) => {
        return {
          sign: this.resultAsBase64 ? s : base64ToArrayBuffer(s),
          uid: this.files[i].uid
        };
      })
      : [];
  }

  private async signData(): Promise<Array<any>> {
    let signs: Array<any> = [];
    let { certificate, privateKeyPassword } = this.signingParams.data;
    const hashAlgo = determineHashAlgorithm(this.keyType);

    for (let file of this.files) {
      try {
        let fileContent = this.getFileContent(file);
        if (!fileContent) continue;

        let isInternal = await this.isDataIsInternalSignature(fileContent);

        let { recaptchaToken, recaptchaType } = await getRecaptchaTokenIfNotAuthenticated();

        let signResult: any;

        if (isInternal) {
          let hash = await this.euSignLib.HashData(hashAlgo, fileContent, true);

          let signedHashes = await cloudKeySignService.signHashes(certificate, privateKeyPassword, [hash], recaptchaToken, recaptchaType);
          if (!signedHashes || signedHashes.length !== 1) {
            throw new Error('Failed to sign hash');
          }

          let creationResult = await utilityService.createInternalNestedSign(arrayBufferToBase64(fileContent), signedHashes[0]);
          if (!creationResult || !creationResult.newSignature) {
            throw new Error('Failed to create signature');
          }

          signResult = [creationResult.newSignature];
        } else {
          signResult = await cloudKeySignService.signFiles(certificate, privateKeyPassword, [arrayBufferToBase64(fileContent)],
            recaptchaToken, recaptchaType);
        }

        if (!signResult || signResult.length < 1) {
          throw new Error('Unknown error');
        }
        signs.push({ sign: signResult[0], uid: file.uid });
      } catch (error) {
        if (this.ignoreErrors) {
          logger.error(`Signing file uid: ${file.uid} error: `, error);
          signs.push({
            uid: file.uid,
            error: error
          });
        } else {
          throw error;
        }
      }
    }

    await this.fillEndUserOwnerInfoFromSigns(signs, (s) => s.sign);

    return signs.map(s => {
      if (!this.resultAsBase64) {
        s.sign = base64ToArrayBuffer(s.sign);
      }
      return s;
    });
  }
}

export class SignFilesWithQrCloudProviderStrategy extends SignFilesCloudStrategy {
  protected getOwnCertificates(): Promise<any> {
    return this.euSignLib.GetOwnCertificates();
  }

  protected async euSignHash(
    signAlgo: number,
    hash: string | Uint8Array | Array<string | Uint8Array>,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return this.__euSign('SignHash', [signAlgo, hash, appendCert, asBase64String]);
  }

  protected async euSignData(
    signAlgo: number,
    data: string | Uint8Array | Array<string | Uint8Array>,
    external: boolean,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return this.__euSign('SignDataEx', [signAlgo, data, external, appendCert, asBase64String]);
  }

  private async __euSign(
    operationName: string,
    operationParams: Array<any>
  ): Promise<any> {
    try {
      return await executeCloudSignOperationWithConfirmationByQrCode(
        this.signingParams.signProvider,
        this.euSignLib,
        operationName,
        operationParams,
        this.signingParams.privateKeyType,
        AppSignKeyOperationType.Signing,
        this.mobileAppName
      );
    } catch (error) {
      if (/cancelled/i.test(error as any)) {
        EventBus.$emit('app-sign-reset-private-key', {
          privateKeyType: this.signingParams?.privateKeyType
        });
      }
      throw error;
    }
  }
}

export class SignFilesWithIdCloudProviderStrategy extends SignFilesCloudStrategy {
  protected getOwnCertificates(): Promise<any> {
    return this.euSignLib.GetOwnCertificates();
  }

  protected async euSignHash(
    signAlgo: number,
    hash: string | Uint8Array | Array<string | Uint8Array>,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return this.__euSign('SignHash', [signAlgo, hash, appendCert, asBase64String]);
  }

  protected async euSignData(
    signAlgo: number,
    data: string | Uint8Array | Array<string | Uint8Array>,
    external: boolean,
    appendCert: boolean,
    asBase64String: boolean): Promise<any> {
    return this.__euSign('SignDataEx', [signAlgo, data, external, appendCert, asBase64String]);
  }

  private async __euSign(operationName: string, operationParams: Array<any>): Promise<any> {
    setLoadingCancelCallback(() => this.euSignLib.ResetOperationKSP());

    setLoadingText({
      text: getLocale('appSignCloudProviderDefault.selectKeyInMobileApp', { mobileAppName: this.mobileAppName }).toString()
    });

    try {
      return await this.euSignLib[operationName].apply(this.euSignLib, operationParams);
    } finally {
      setLoadingCancelCallback(null);

      setLoadingText({ text: '' });
    }
  }
}
